import React, { useState, useContext, useEffect } from 'react'
import { LanguageContext } from '../../i18n'
import api from 'services/api';
import AssetsContext from 'hooks/AssetsProvider/AssetsContext'
import Utils from 'utils/utils'

const AssetsProvider = ({ children }) => {
  const [ assets, setAssets ] = useState([]);
  const [ actualColor, setActualColor ] = useState(0);
  const [ benchmarkAssets, setBenchmarkAssets ] = useState([]);
  const [ selectedBenchmarksIndexes, setSelectedBenchmarksIndexes ] = useState([]);
  const { monthNameForDate, shortMonthNameForDate, yearForDate, date2DigitsFormatter, formatPeriod, changeDotToComma, formatCurrency, colorsArray } = Utils();
  const { t } = useContext(LanguageContext);
  const [ isLoading, setIsLoading ] = useState(false);

  const populateAsset = async (asset, period, initialApplication) => {
    return new Promise(async (resolve, reject) => {
      try{
        const assetFoundIndex = findAssetIndexByIdentifierAndType(asset.identifier, asset.assetType);
      
        if(assetFoundIndex === -1){
          // if asset is a benchmark set loading
          const benchmarkIndex = findBenchmarkAsset(asset.identifier, asset.assetType);

          //add benchmark to selected benchmark indexes
          if(benchmarkIndex !== -1){
            setSelectedBenchmarksIndexes([...selectedBenchmarksIndexes, benchmarkIndex]);
          }

          const assetsPromises = [
            fetchReturnHistory(asset.assetType, asset.identifier),
            fetchRentabilitySeries(asset.assetType, asset.identifier, period, initialApplication),
            fetchReturnSeries(asset.assetType, asset.identifier, period),
            processIndicatorsRentability(asset.assetType, asset.identifier, period, initialApplication),
            processIndicatorsReturn(asset.assetType, asset.identifier, period),
            processIndicatorsDrawdown(asset.assetType, asset.identifier, period),
            fetchDrawdownSeries(asset.assetType, asset.identifier, period), 
          ]

          const resolvedAssetsPromises = await Promise.allSettled(assetsPromises);
          
          // add returnHistoryData
          if(resolvedAssetsPromises[0].status === 'fulfilled'){
            const processedHistoryData = processReturnHistoryData(resolvedAssetsPromises[0].value.data);
            asset.returnHistoryData = processedHistoryData;
          }

          // add rentability series 
          if(resolvedAssetsPromises[1].status === 'fulfilled'){
            const processedRentabilitySeries = processSeries(resolvedAssetsPromises[1].value.data)
            asset.rentabilitySeries = processedRentabilitySeries;
          }

          // add return series 
          if(resolvedAssetsPromises[2].status === 'fulfilled'){
            const processedReturnSeries = processSeries(resolvedAssetsPromises[2].value.data, true)
            asset.returnSeries = processedReturnSeries;
          }

          // add indicators data
          if(resolvedAssetsPromises[3].status === 'fulfilled'){
            asset.indicatorsRentabilityData = resolvedAssetsPromises[3].value;
          }

          // add indicators data
          if(resolvedAssetsPromises[4].status === 'fulfilled'){
            asset.indicatorsReturnData = resolvedAssetsPromises[4].value;
          }

          // add indicators data
          if(resolvedAssetsPromises[5].status === 'fulfilled'){
            asset.indicatorsDrawdownData = resolvedAssetsPromises[5].value;
          }

          // add return series 
          if(resolvedAssetsPromises[6].status === 'fulfilled'){
            const processedDrawdownSeries = processSeries(resolvedAssetsPromises[6].value.data, true);
            asset.drawdownSeries = processedDrawdownSeries;
          }
          resolve(asset);
        }
        resolve(undefined)
      }catch(err){
        reject(err);
      }
    })
    
  }

  const addColor = (asset) => {
    // add color to asset
    if(!asset.color){
      if(actualColor  === colorsArray.length){
        asset.color = colorsArray[0];
        setActualColor(1);
      }else{
        asset.color = colorsArray[actualColor];
        setActualColor(actualColor + 1);
      }
    }
    return asset;
  }

  const addAsset = async (asset, period, initialApplication) => {
      setIsLoading(true);
      const populatedAsset = await populateAsset(asset, period, initialApplication);
      if(populatedAsset){
        const assetComplete = addColor(populatedAsset)
        setAssets([...assets, assetComplete])
      }
      setIsLoading(false);
  } 

  const addAssetsBatch = async (assetsToBatch, period, initialApplication) => {
    try{
      let pAssets = [];
      if(assetsToBatch.length > 0){
        for(let i=0; i<assetsToBatch.length; i++){
          const pAsset = await populateAsset(assetsToBatch[i], period, initialApplication);
          if(i === 0){
            pAsset = addColor(pAsset)
          }
          pAssets = [...pAssets, pAsset]
        }
        setAssets([...assets, ...pAssets]);
        // setIsLoading(false);
      }
    }catch(err){
      console.log(err)
    }
  }

  const generatePeriodString = (tab) => {
    if(assets.length > 0){
      for(const asset of assets) {
        switch(tab){
          case 0:
            if(asset.rentabilitySeries.length > 0){
              const rentabilitySeries = asset.rentabilitySeries
              const initialDate = rentabilitySeries[0][0]
              const finalDate = rentabilitySeries[rentabilitySeries.length - 1][0];
              return formatPeriod(finalDate, initialDate);
            }
            break;
          case 1:
            if(asset.returnSeries.length > 0){
              const returnSeries = asset.returnSeries
              const initialDate = returnSeries[0][0]
              const finalDate = returnSeries[returnSeries.length - 1][0];
              return formatPeriod(finalDate, initialDate);
            }
            break;
          case 2:
            if(asset.returnSeries.length > 0){
              const drawdownSeries = asset.drawdownSeries
              const initialDate = drawdownSeries[0][0]
              const finalDate = drawdownSeries[drawdownSeries.length - 1][0];
              return formatPeriod(finalDate, initialDate);
            }
            break;
        }
      };
    }
    return "";
  }

  const updateWhenChangesPeriodOrInitialApplication = async (period, initialApplication) => {
    const clonedAssets = [...assets];
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      try{
        for(let i=0; i < clonedAssets.length; i+=1){
          const assetsPromises = [
            fetchRentabilitySeries(clonedAssets[i].assetType, clonedAssets[i].identifier, period, initialApplication),
            fetchReturnSeries(clonedAssets[i].assetType, clonedAssets[i].identifier, period),
            processIndicatorsRentability(clonedAssets[i].assetType, clonedAssets[i].identifier, period, initialApplication),
            processIndicatorsReturn(clonedAssets[i].assetType, clonedAssets[i].identifier, period),
            processIndicatorsDrawdown(clonedAssets[i].assetType, clonedAssets[i].identifier, period),
            fetchDrawdownSeries(clonedAssets[i].assetType, clonedAssets[i].identifier, period),
          ]
          const assetsPromisesResolved = await Promise.allSettled(assetsPromises);
          
          //update rentability series
          if(assetsPromisesResolved[0].status === 'fulfilled'){
            const processedRentabilitySeries = processSeries(assetsPromisesResolved[0].value.data)
            clonedAssets[i].rentabilitySeries = processedRentabilitySeries;
          }
          
          //update return series
          if(assetsPromisesResolved[1].status === 'fulfilled'){
            const processedReturnSeries = processSeries(assetsPromisesResolved[1].value.data, true)
            clonedAssets[i].returnSeries = processedReturnSeries;
          }

          //update indicators data
          if(assetsPromisesResolved[2].status === 'fulfilled'){
            clonedAssets[i].indicatorsRentabilityData = assetsPromisesResolved[2].value;
          }
          //update indicators return data
          if(assetsPromisesResolved[3].status === 'fulfilled'){
            clonedAssets[i].indicatorsReturnData = assetsPromisesResolved[3].value;
          }

          //update indicators drawdown data
          if(assetsPromisesResolved[4].status === 'fulfilled'){
            clonedAssets[i].indicatorsDrawdownData = assetsPromisesResolved[4].value;
          }

          //update drawdown series
          if(assetsPromisesResolved[5].status === 'fulfilled'){
            const processedDrawdownSeries = processSeries(assetsPromisesResolved[5].value.data, true)
            clonedAssets[i].drawdownSeries = processedDrawdownSeries;
          }
        }
        setAssets(clonedAssets);
        resolve('ok');
        setIsLoading(false);
      }catch(err){
        reject(err);
        setIsLoading(false);
      }
    })
  }

  const fetchBenchmarksAssets = async () => {
    try{
      const benchMarks = await api.get('asset/indices');
      setBenchmarkAssets(benchMarks.data)
    }catch(err){
      console.log(err)
    }
  }

  const findBenchmarkAsset = (assetIdentifier, assetType) => { 
    let benchmarkAssetIndexFounded = -1;
    for(let i=0; i<benchmarkAssets.length; i++){
      if(benchmarkAssets[i].assetType === assetType && benchmarkAssets[i].identifier === assetIdentifier){
        benchmarkAssetIndexFounded = i;
        break;
      }
    }
    return benchmarkAssetIndexFounded;
  }

  const fetchRentabilitySeries = async (assetType, assetIdentifier, period, initialApplication) => {
    try{
      return api.get(`calculation/${assetType}/${assetIdentifier}/return/series?period=${period}&initialApplication=${initialApplication}`);
    }catch(err){
      console.log(err)
    }
  }
  
  const fetchReturnSeries = async (assetType, assetIdentifier, period) => {
    try{
      return api.get(`calculation/${assetType}/${assetIdentifier}/return/cumulative/series?period=${period}`);
    }catch(err){
      console.log(err)
    }
  }

  const fetchDrawdownSeries = async (assetType, assetIdentifier, period) => {
    try{
      return api.get(`calculation/${assetType}/${assetIdentifier}/drawdown/series?period=${period}`);
    }catch(err){
      console.log(err)
    }
  }

  const fetchReturnHistory = async (assetType, assetIndentifier) => {
    try{
      return api.get(`calculation/${assetType}/${assetIndentifier}/return/history`);
    }catch(err){
      console.log(err)
    }
  }

  const fetchIndicator = async (assetType, assetIdentifier, measure, period, initialApplication=false) => {
    try{
      return api.get(`calculation/${assetType}/${assetIdentifier}/${measure}?period=${period}${initialApplication ? `&initialApplication=${initialApplication}` : '' }`);
    }catch(err){
      console.log(err)
    }
  }

  const formatValue = (value, format) => {
    const vauleC = changeDotToComma(Number(Number(value).toFixed(2)));

    switch(format){
      case 'CURRENCY':
        return formatCurrency(value);
      case 'PERCENTAGE':
        return`${vauleC}%`;
      case 'NUMBER':
        return`${vauleC}`;
      case 'DATE':
        let date = new Date(value);
        return date2DigitsFormatter.format(date && date.setHours(date.getHours() + 3));
      default:
        return vauleC
    }
  }

  const isAssetFund = (assetType) => ['FI', 'FII', 'EUROPA'].includes(assetType);

  const processIndicatorsRentability = async (assetType, assetIdentifier, period, initialApplication) => {
    const measures = ['RENTABILIDADE', 'VOLATILIDADE', 'SHARPE', 'TAXA_DE_ADMINISTRACAO', 'PATRIMONIO_LIQUIDO' ];
    let indicatorsProcessed = {}
    let indicatorsPromises = [];

    for(let i=0; i<measures.length; i+=1){
      indicatorsPromises = [...indicatorsPromises, fetchIndicator(assetType, assetIdentifier, measures[i], period, initialApplication)]
    }

    const resolvedIndicators = await Promise.allSettled(indicatorsPromises);

    for(let i=0; i<measures.length; i+=1){
      if(resolvedIndicators[i].status === 'fulfilled'){
        indicatorsProcessed[t[`measure:${measures[i]}`]] = 
        !isAssetFund(assetType) && (measures[i] === 'TAXA_DE_ADMINISTRACAO' || measures[i] === 'PATRIMONIO_LIQUIDO') ? 
        {
          value: 'n/a', 
          period: formatPeriod(resolvedIndicators[i].value.data.finalDate, resolvedIndicators[i].value.data.initialDate)
        } : 
        {
          value: resolvedIndicators[i].value.data.value !== null ? formatValue(resolvedIndicators[i].value.data.value, resolvedIndicators[i].value.data.format) : '-', 
          period: formatPeriod(resolvedIndicators[i].value.data.finalDate, resolvedIndicators[i].value.data.initialDate)
        };
      }
    }

    return indicatorsProcessed;
  }

  const processIndicatorsReturn = async (assetType, assetIdentifier, period) => {
    const measures = ['RETORNO', 'RETORNO_UM_ANO', 'RETORNO_YTD'];
    let indicatorsProcessed = {}
    let indicatorsPromises = [];

    for(let i=0; i<measures.length; i+=1){
      indicatorsPromises = [...indicatorsPromises, fetchIndicator(assetType, assetIdentifier, measures[i], period)]
    }

    const resolvedIndicators = await Promise.allSettled(indicatorsPromises);

    for(let i=0; i<measures.length; i+=1){
      if(resolvedIndicators[i].status === 'fulfilled'){
        indicatorsProcessed[t[`measure:${measures[i]}`]] = 
        {
          value: resolvedIndicators[i].value.data.value !== null ? formatValue(resolvedIndicators[i].value.data.value, resolvedIndicators[i].value.data.format) : '-', 
          period: formatPeriod(resolvedIndicators[i].value.data.finalDate, resolvedIndicators[i].value.data.initialDate)
        };
      }
    }

    return indicatorsProcessed;
  }

  const processIndicatorsDrawdown = async (assetType, assetIdentifier, period) => {
    const measures = ['DRAW_DOWN', 'INICIO_DO_1_DRAW_DOWN', 'DATA_DO_1_DRAW_DOWN', 'PRIMEIRO_DRAW_DOWN'];
    let indicatorsProcessed = {}
    let indicatorsPromises = [];

    for(let i=0; i<measures.length; i+=1){
      indicatorsPromises = [...indicatorsPromises, fetchIndicator(assetType, assetIdentifier, measures[i], period)]
    }

    const resolvedIndicators = await Promise.allSettled(indicatorsPromises);

    for(let i=0; i<measures.length; i+=1){
      if(resolvedIndicators[i].status === 'fulfilled'){
        indicatorsProcessed[t[`measure:${measures[i]}`]] = 
        {
          value: resolvedIndicators[i].value.data.value !== null ? formatValue(resolvedIndicators[i].value.data.value, resolvedIndicators[i].value.data.format) : '-', 
          period: formatPeriod(resolvedIndicators[i].value.data.finalDate, resolvedIndicators[i].value.data.initialDate)
        };
      }
    }

    return indicatorsProcessed;
  }

  const processReturnHistoryData = (data) => {
    let processedReturnHistoryData = {}
    data.forEach(value => {
      const columnName = `${shortMonthNameForDate(value.finalDate)}. ${yearForDate(value.finalDate)}`;
      processedReturnHistoryData[columnName] = Number(Number(value.value).toFixed(2))
    })
    return processedReturnHistoryData;
  }

  const processSeries = (data, transformToPercent) => {
    return data.map(serieItem => {
      const dateTimestamp = new Date(serieItem.date).valueOf();
      const value = transformToPercent ? Number(Number(serieItem.value * 100).toFixed(2)) : Number(Number(serieItem.value).toFixed(2));

      return [ dateTimestamp, value ];
    })
  }

  const findAssetIndexByIdentifierAndType = (assetIdentifier, assetType) => {
    let assetFoundIndex = -1;
    for(let i = 0; i < assets.length; i+=1){
      if(assets[i].identifier === assetIdentifier && assets[i].assetType === assetType){
        assetFoundIndex = i;  
        break;
      }
    }
    return assetFoundIndex;
  } 

  const changeAssetColor = (color, assetIdentifier, assetType) => {
    const assetFoundIndex = findAssetIndexByIdentifierAndType(assetIdentifier, assetType);

    if(assetFoundIndex !== -1){
      let clonedAssets = [...assets];
      clonedAssets[assetFoundIndex].color = color;
      setAssets(clonedAssets);
    }
  }

  const removeAsset = (assetIdentifier, assetType) => {
    const assetFoundIndex = findAssetIndexByIdentifierAndType(assetIdentifier, assetType);
    
    if(assetFoundIndex !== -1){
      const benchmarkIndex = findBenchmarkAsset(assetIdentifier, assetType);
      
      if(benchmarkIndex !== -1){
        if(selectedBenchmarksIndexes.length === 1) return;
        setSelectedBenchmarksIndexes(selectedBenchmarksIndexes.filter(selectedBenchmarkIndex => selectedBenchmarkIndex !== benchmarkIndex ));
      }

      const newArray = [...assets];
      newArray.splice(assetFoundIndex,1);
      setAssets(newArray);
    }
  }

  const removeAllAssets = () => {
    setAssets([]);
    setSelectedBenchmarksIndexes([]);
  }

  useEffect(async () => {
    await fetchBenchmarksAssets();
  }, []);

  return (
      <AssetsContext.Provider 
        value={{ 
          assets, 
          setAssets, 
          addAsset, 
          changeAssetColor, 
          removeAsset, 
          updateWhenChangesPeriodOrInitialApplication, 
          benchmarkAssets, 
          selectedBenchmarksIndexes,
          setSelectedBenchmarksIndexes,
          findBenchmarkAsset,
          isLoadingAssets: isLoading,
          generatePeriodString,
          actualColor,
          setActualColor,
          addAssetsBatch,
          setIsLoadingAssets: setIsLoading,
          removeAllAssets,
          addColor
        }}>
        {children}
      </AssetsContext.Provider>
  )
}

export default AssetsProvider
